import React from 'react';
import clsx from 'clsx';
import Callout from 'components/rtmEnrollment/Callout';
import IconTile from 'components/rtmEnrollment/IconTile';
import InfoRow from 'components/rtmEnrollment/InfoRow';
import type {
  EnrollmentIcon,
  EnrollmentSection,
} from 'components/rtmEnrollment/enrollmentSteps';
import { getRtmEnrollmentStyles } from 'components/rtmEnrollment/rtmEnrollmentStyles';

type EnrollmentStepSectionsProps = {
  sections: EnrollmentSection[];
};

function renderSectionIcon(icon: EnrollmentIcon) {
  return <IconTile icon={icon} size="sm" />;
}

function getSectionKey(section: EnrollmentSection): string {
  if (section.type === 'paragraph') {
    return `paragraph-${typeof section.content === 'string' ? section.content : 'node'}`;
  }

  if (section.type === 'feature-list') {
    const firstItem = section.items[0];
    return `feature-list-${typeof firstItem?.text === 'string' ? firstItem.text : 'node'}`;
  }

  if (section.type === 'info-rows') {
    const firstItem = section.items[0];
    return `info-rows-${typeof firstItem?.label === 'string' ? firstItem.label : 'node'}`;
  }

  if (section.type === 'cards') {
    const firstItem = section.items[0];
    return `cards-${typeof firstItem?.title === 'string' ? firstItem.title : 'node'}`;
  }

  return `callout-${typeof section.content === 'string' ? section.content : 'node'}`;
}

function SectionContent({ section }: { section: EnrollmentSection }) {
  const styles = getRtmEnrollmentStyles();
  const toneIconClassName = styles.icon.tone;

  return (
    <>
      {section.type === 'paragraph' && (
        <p className={styles.type.body}>
          {section.content}
        </p>
      )}

      {section.type === 'feature-list' && (
        <div className={styles.space.listStack}>
          {section.items.map((item) => (
            <div
              key={typeof item.text === 'string' ? item.text : String(item.text)}
              className={styles.space.featureItem}
            >
              {renderSectionIcon(item.icon)}
              <p className={styles.type.featureText}>
                {item.text}
              </p>
            </div>
          ))}
        </div>
      )}

      {section.type === 'info-rows' && (
        <div className={styles.space.listStack}>
          {section.items.map((item) => (
            <InfoRow
              key={typeof item.label === 'string' ? item.label : String(item.label)}
              icon={item.icon}
              label={item.label}
              description={item.description}
            />
          ))}
        </div>
      )}

      {section.type === 'cards' && (
        <div className={styles.space.cardStack}>
          {section.items.map((item) => {
            const Icon = item.icon;
            const cardTone = item.tone ?? 'neutral';

            return (
              <div
                key={typeof item.title === 'string' ? item.title : String(item.title)}
                className={clsx(styles.space.cardItem, styles.cardTone[cardTone])}
              >
                <div className={styles.space.cardItemHeader}>
                  <Icon
                    className={clsx(styles.icon.cardToneIcon, toneIconClassName[cardTone])}
                    aria-hidden="true"
                  />
                  <p className={styles.type.cardTitle}>
                    {item.title}
                  </p>
                </div>
                <p
                  className={clsx(
                    styles.space.cardDescriptionOffset,
                    styles.type.cardDescription,
                  )}
                >
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {section.type === 'callout' && (
        <Callout>
          {section.content}
        </Callout>
      )}
    </>
  );
}

export default function EnrollmentStepSections({ sections }: EnrollmentStepSectionsProps) {
  const styles = getRtmEnrollmentStyles();

  return (
    <div className={styles.space.sectionStack}>
      {sections.map((section) => (
        <SectionContent
          key={getSectionKey(section)}
          section={section}
        />
      ))}
    </div>
  );
}
